<!--
  Energized Electrical Work Permit Form
  
  Step-by-step permit submission with:
  - 6 steps: Basic Info, Requester Info, Hazards, Qualified Person, PPE, Approvals
  - Checkbox groups for safety requirements and PPE with dynamic label mapping
  - Form validation for electrical work safety compliance
  - Signature requirements on final step
-->

<!-- Use the form layout component to handle the wizard UI -->
<app-form-layout
  [title]="formTitle"
  [form]="form"
  [icon]="formIcon"
  [disabled]="submitting"
  [onSubmit]="onSubmit.bind(this)"
  [steps]="steps"
  [currentStep]="currentStep()"
  [completedSteps]="completedSteps()"
  [canNavigateToStep]="canNavigateToStep.bind(this)"
  [isStepCompleted]="isStepCompleted.bind(this)"
  [onNextStep]="onNextStep.bind(this)"
  [onPreviousStep]="onPreviousStep.bind(this)"
  [getProgressPercentage]="getProgressPercentage.bind(this)"
>
  <div
    *ngIf="form && fieldMappings"
    formContent
    [formGroup]="form"
    class="form-content"
  >
    <!-- Basic Information Step -->
    <div *ngIf="currentStep() === steps[0].id" class="step-content">
      <ion-list>
        <ion-item-divider class="form-step-title">
          <ion-label>
            <h1>{{ steps[0].title }}</h1>
            <p>{{ steps[0].description }}</p>
          </ion-label>
        </ion-item-divider>

        <ion-grid>
          <ion-row> </ion-row>
          <ion-row>
            <ion-col>
              <app-text-input
                [templateFormGroup]="basicFormGroup"
                [control]="basicFormGroup.get('jobWorkOrderName')!"
                [label]="fieldMappings['jobWorkOrderName']"
                [requiredField]="requiredFields['jobWorkOrderName']"
                placeholder="Enter job/work order name"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col>
              <app-text-input
                [templateFormGroup]="basicFormGroup"
                [control]="basicFormGroup.get('jobWorkOrderNumber')!"
                [label]="fieldMappings['jobWorkOrderNumber']"
                [requiredField]="requiredFields['jobWorkOrderNumber']"
                placeholder="Enter job/work order number"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
        </ion-grid>
      </ion-list>
    </div>

    <!-- Requester Information Step -->
    <div *ngIf="currentStep() === steps[1].id" class="step-content">
      <ion-list>
        <ion-item-divider class="form-step-title">
          <ion-label>
            <h1>{{ steps[1].title }}</h1>
            <p>{{ steps[1].description }}</p>
          </ion-label>
        </ion-item-divider>

        <ion-grid>
          <ion-row>
            <ion-col>
              <app-textarea-input
                [templateFormGroup]="requesterFormGroup"
                [control]="
                  requesterFormGroup.get('circuitEquipmentDescription')!
                "
                [label]="fieldMappings['circuitEquipmentDescription']"
                [requiredField]="requiredFields['circuitEquipmentDescription']"
                [rows]="3"
                placeholder="Enter circuit/equipment description"
              >
              </app-textarea-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col>
              <app-text-input
                [templateFormGroup]="requesterFormGroup"
                [control]="
                  requesterFormGroup.get('upstreamOverCurrentDeviceLocation')!
                "
                [label]="fieldMappings['upstreamOverCurrentDeviceLocation']"
                [requiredField]="
                  requiredFields['upstreamOverCurrentDeviceLocation']
                "
                placeholder="Enter location"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col>
              <app-textarea-input
                [templateFormGroup]="requesterFormGroup"
                [control]="requesterFormGroup.get('workDescription')!"
                [label]="fieldMappings['workDescription']"
                [requiredField]="requiredFields['workDescription']"
                [rows]="3"
                placeholder="Enter work description"
              >
              </app-textarea-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col>
              <app-textarea-input
                [templateFormGroup]="requesterFormGroup"
                [control]="
                  requesterFormGroup.get('justificationForEnergizedWork')!
                "
                [label]="fieldMappings['justificationForEnergizedWork']"
                [requiredField]="
                  requiredFields['justificationForEnergizedWork']
                "
                [rows]="3"
                placeholder="Enter justification"
              >
              </app-textarea-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col>
              <app-text-input
                [templateFormGroup]="requesterFormGroup"
                [control]="requesterFormGroup.get('requesterTitle')!"
                [label]="fieldMappings['requesterTitle']"
                [requiredField]="requiredFields['requesterTitle']"
                placeholder="Enter requester title"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col>
              <app-date-input
                [templateFormGroup]="requesterFormGroup"
                [control]="requesterFormGroup.get('requesterDate')!"
                [label]="fieldMappings['requesterDate']"
                [requiredField]="requiredFields['requesterDate']"
                [today]="true"
              >
              </app-date-input>
            </ion-col>
          </ion-row>
        </ion-grid>
      </ion-list>
    </div>

    <!-- Hazards Step -->
    <div *ngIf="currentStep() === steps[2].id" class="step-content">
      <ion-list>
        <ion-item-divider class="form-step-title">
          <ion-label>
            <h1>{{ steps[2].title }}</h1>
            <p>{{ steps[2].description }}</p>
          </ion-label>
        </ion-item-divider>
        <ion-grid>
          <ion-row>
            <ion-col>
              <app-textarea-input
                [templateFormGroup]="hazardsFormGroup"
                [control]="hazardsFormGroup.get('electricalHazards')!"
                [label]="fieldMappings['electricalHazards']"
                [requiredField]="requiredFields['electricalHazards']"
                [rows]="3"
              ></app-textarea-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col>
              <app-textarea-input
                [templateFormGroup]="hazardsFormGroup"
                [control]="hazardsFormGroup.get('mechanicalHazards')!"
                [label]="fieldMappings['mechanicalHazards']"
                [requiredField]="requiredFields['mechanicalHazards']"
                [rows]="3"
              ></app-textarea-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col>
              <app-textarea-input
                [templateFormGroup]="hazardsFormGroup"
                [control]="hazardsFormGroup.get('workingSpaceConstraints')!"
                [label]="fieldMappings['workingSpaceConstraints']"
                [requiredField]="requiredFields['workingSpaceConstraints']"
                [rows]="3"
              ></app-textarea-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col>
              <app-textarea-input
                [templateFormGroup]="hazardsFormGroup"
                [control]="hazardsFormGroup.get('otherHazards')!"
                [label]="fieldMappings['otherHazards']"
                [rows]="3"
              ></app-textarea-input>
            </ion-col>
          </ion-row>
        </ion-grid>
      </ion-list>
    </div>

    <!-- Qualified Person Step -->
    <div *ngIf="currentStep() === steps[3].id" class="step-content">
      <ion-list>
        <ion-item-divider class="form-step-title">
          <ion-label>
            <h1>{{ steps[3].title }}</h1>
            <p>{{ steps[3].description }}</p>
          </ion-label>
        </ion-item-divider>

        <ion-grid>
          <ion-row>
            <ion-col>
              <app-checkbox-input
                [templateFormGroup]="checkWhenCompleteFormGroup"
                [checkboxes]="checkWhenCompleteCheckboxes"
                [label]="fieldMappings['checkWhenComplete']"
              >
              </app-checkbox-input>
            </ion-col>
          </ion-row>

          <ion-row>
            <ion-col>
              <app-radio-group-input
                [templateFormGroup]="qualifiedPersonFormGroup"
                [control]="
                  qualifiedPersonFormGroup.get('agreeWorkCanBeDoneSafely')!
                "
                [label]="fieldMappings['agreeWorkCanBeDoneSafely']"
                [requiredField]="requiredFields['agreeWorkCanBeDoneSafely']"
                [options]="['Yes', 'No']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
          </ion-row>
        </ion-grid>
      </ion-list>
    </div>

    <!-- PPE Step -->
    <div *ngIf="currentStep() === steps[4].id" class="step-content">
      <ion-list>
        <ion-item-divider class="form-step-title">
          <ion-label>
            <h1>{{ steps[4].title }}</h1>
            <p>{{ steps[4].description }}</p>
          </ion-label>
        </ion-item-divider>
<ion-grid>
  <ion-row>
    <ion-col>
      <app-checkbox-input
      [templateFormGroup]="ppeCheckboxesFormGroup"
      [checkboxes]="ppeCheckboxes"
      [label]="fieldMappings['ppeCheckboxes']">
    </app-checkbox-input> 
    </ion-col>
  </ion-row>

     <ion-row *ngIf="ppeCheckboxesFormGroup.get('otherPPE')?.value">
      <ion-col>
          <app-text-input
            [templateFormGroup]="ppeFormGroup"
            [control]="ppeFormGroup.get('otherPPEDescription')!"
            [label]="fieldMappings['otherPPEDescription']"
            [rows]="3"
            placeholder="Describe other PPE requirements"
          >
          </app-text-input>
        </ion-col>
      </ion-row>
    </ion-grid>
  </ion-list>
</div>

    
 <!-- PPE Step -->
 <div *ngIf="currentStep() === steps[5].id" class="step-content">
  <ion-list>
    <ion-item-divider class="form-step-title" >
      <ion-label>
        <h1>{{ steps[5].title }}</h1>
        <p>{{ steps[5].description }}</p>
      </ion-label>
    </ion-item-divider>
<ion-grid>
<ion-row >
  <ion-col size="12" size-md="6">
  <app-text-input
  [templateFormGroup]="approvalsFormGroup"
  [control]="approvalsFormGroup.get('electricianName')!"
  [label]="fieldMappings['electricianName']"
  [requiredField]="requiredFields['electricianName']"
  placeholder="Enter electrician name"
  ></app-text-input>  
  </ion-col>
  <ion-col size="12" size-md="6">
    <app-date-input
    [templateFormGroup]="approvalsFormGroup"
    [control]="approvalsFormGroup.get('electricianSignatureDate')!"
    [label]="fieldMappings['electricianSignatureDate']"
    [requiredField]="requiredFields['electricianSignatureDate']"
    [today]="true"
    ></app-date-input>
  </ion-col>
  </ion-row>
<ion-row>
  <ion-col>
    <app-signature-section
    [signatureInfo]="formAttachmentRequirements!.signatures![0]!"
    ></app-signature-section>
  </ion-col>

</ion-row>
<ion-row>
  <ion-col size="12" size-md="6">
    <app-text-input
    [templateFormGroup]="approvalsFormGroup"
    [control]="approvalsFormGroup.get('supervisorName')!"
    [label]="fieldMappings['supervisorName']"
    [requiredField]="requiredFields['supervisorName']"
    placeholder="Enter supervisor name"
    ></app-text-input>
  </ion-col>
  <ion-col size="12" size-md="6">
    <app-date-input
    [templateFormGroup]="approvalsFormGroup"
    [control]="approvalsFormGroup.get('supervisorSignatureDate')!"
    [label]="fieldMappings['supervisorSignatureDate']"
    [requiredField]="requiredFields['supervisorSignatureDate']"
    [today]="true"
    ></app-date-input>
  </ion-col>
</ion-row>
<ion-row>
  <ion-col>
    <app-signature-section
    [signatureInfo]="formAttachmentRequirements!.signatures![1]!"
    ></app-signature-section>
  </ion-col>
 
</ion-row>
</ion-grid>

      </ion-list>
    </div>
  </div>
</app-form-layout>
